import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Colors, filterFromTable, Images, SavePage, SaveParams } from 'utils';
import { MenuTool } from './fullTableCore/menu';
import { MinLoader } from '../loader';
import { handleSelectSort } from './fullTableCore/constants';
import { TextRow } from '../table';
import { useDebouncedCallback } from 'use-debounce';

export const SearchAndFilerFull = ({
    width,
    item,
    loader,
    setListInfo,
    list,
    searched,
    setSearched,
    generateTable,
    getParams,
    savedParams,
}) => {
    const [backSearched, setBackSearched] = useState({ searchKey: '', searchValue: '' });
    const navigate = useNavigate();
    const location = useLocation();
    const info = savedParams ? savedParams : location?.state;
    /** This column's own sort direction — `null` unless the grid is sorted by *this* column. */
    const sortedBy = info?.sortType === item?.name ? info?.orderType : null;
    const active =
        info?.[item?.name] === item?.name
            ? true
            : info?.hasOwnProperty(item?.name)
              ? true
              : info?.sortType === item?.name
                ? true
                : (searched?.name === item?.name && searched?.value) ||
                  (backSearched?.searchKey === item?.searchKey && backSearched?.searchValue);

    useEffect(() => {
        if (info?.searchKey) {
            setBackSearched({
                searchKey: info?.searchKey,
                searchValue: info?.searchValue,
            });
        } else {
            setBackSearched({
                searchKey: '',
                searchValue: '',
            });
        }
    }, [info]);

    const debounced = useDebouncedCallback(
        (name, value, searchKey) => {
            const params = {
                ...info,
            };
            params.limit = 100;
            params.skip = 0;
            params.page = 1;
            if (value) {
                params.searchKey = searchKey;
                params.searchValue = value;
            } else {
                delete params.searchKey;
                delete params.searchValue;
            }
            if (getParams) {
                getParams(params);
            } else {
                SaveParams(navigate, params);
            }
        },
        1000,
        { maxWait: 2000 }
    );

    const handleSearch = (e, nameRow, searchKey, type) => {
        if (searchKey) {
            setBackSearched({
                searchKey: searchKey,
                searchValue: e.target.value,
            });
            debounced(e.target.name, e.target.value, searchKey);
        } else {
            setSearched({
                name: e.target.name,
                value: e.target.value,
            });

            if (e.target.name) {
                if (!e.target.value) {
                    setListInfo(list);
                } else {
                    const info = filterFromTable(e, nameRow, list, type);
                    setListInfo(info);
                }
            }
        }
    };

    /**
     * Three-state sort on a column header: A→Z, then Z→A, then off.
     *
     * "Off" deletes both keys rather than parking on a direction, so the list falls back to the order
     * it has when nothing is asked for — `fullName ASC` for staff (the service fills it in), newest
     * first for clients (the API's own `createdAt: -1`). That default is otherwise unreachable: sort
     * state rides in `location.state`, which both survives a reload and is persisted per user, so a
     * cycle with no off position means one stray click changes the grid's order for good.
     *
     * The direction is read off `sortType` too, not off `orderType` alone: a fresh column always
     * starts its own cycle at ASC. Reading `orderType` on its own is how clicking column B used to
     * inherit column A's direction and open on Z→A.
     */
    const handleClickSort = (name) => {
        if (item?.custom === false) return;

        const current = info?.sortType === name ? info?.orderType : null;
        const next = current === 'ASC' ? 'DESC' : current === 'DESC' ? null : 'ASC';

        const anotherInfo = {
            ...info,
        };
        if (next) {
            anotherInfo.sortType = name;
            anotherInfo.orderType = next;
        } else {
            delete anotherInfo.sortType;
            delete anotherInfo.orderType;
        }

        if (getParams) {
            getParams(anotherInfo);
        } else {
            SavePage(navigate, info, { ...anotherInfo });
        }
    };

    const handleFilter = (e) => {
        handleSelectSort(e, navigate, info, getParams);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        // Handle form submission
    };

    // Three states, one icon: ASC and DESC each show their own arrow at full opacity, and unsorted
    // is the ASC arrow dimmed — a preview of what the next click does.
    const sortIcon = (
        <img
            style={sortedBy ? { opacity: 1 } : { opacity: 0.6 }}
            src={
                item?.iconType === 'date'
                    ? sortedBy === 'DESC'
                        ? Images.latestEarliest
                        : Images.earliestLatestBlue
                    : sortedBy === 'DESC'
                      ? Images.zToA
                      : Images.aToZ
            }
            alt={'sort icon'}
        />
    );

    return (
        <div className="search-and-filer-box">
            <div className={active ? 'active-box' : 'passive-box'} style={width ? { width: width } : {}}>
                {!item?.filterList && (
                    <div
                        onClick={() => handleClickSort(item?.name)}
                        className="search-input-title"
                        style={{ cursor: item?.custom !== false ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                    >
                        <TextRow name={item?.title} textWidth={8} fontWeight={600} />
                        {item?.custom !== false && (
                            <div className={generateTable ? 'sort-btn-box-generate' : 'sort-btn-box'}>{sortIcon}</div>
                        )}
                    </div>
                )}

                {item?.custom !== false && item?.filterList && (
                    <MenuTool
                        item={item}
                        handleFilter={handleFilter}
                        selectedFilter={info?.type ? info?.type : 'All'}
                        getParams={getParams}
                        savedParams={savedParams}
                        sortControl={
                            item?.sortable && (
                                <button
                                    type="button"
                                    className="item-button"
                                    aria-label={`Sort by ${item?.title}`}
                                    onClick={() => handleClickSort(item?.name)}
                                >
                                    {sortIcon}
                                </button>
                            )
                        }
                    />
                )}

                {!item?.noSearch && (
                    <div style={{ position: 'relative' }}>
                        <form autoComplete="off" onSubmit={handleSubmit}>
                            <input
                                autoComplete={'new-password'}
                                disabled={item?.disabled || !item?.name}
                                name={item?.name}
                                value={
                                    backSearched?.searchKey === item?.searchKey
                                        ? backSearched?.searchValue
                                        : searched?.name === item?.name
                                          ? searched?.value
                                          : ''
                                }
                                // defaultValue={null}
                                // onChange={handleSearch}
                                onChange={(e) => handleSearch(e, item?.rowName, item?.searchKey, item?.icon)}
                                type="search"
                            />
                        </form>
                        {!!loader && <MinLoader margin={'6px 0 0 -20px'} color={Colors.BackgroundBlue} />}
                    </div>
                )}
            </div>
        </div>
    );
};
